import { Component, contentChild, effect, Input, OnChanges, SimpleChanges, TemplateRef, inject } from '@angular/core';
import { fb, globalStore } from '../../../../../shared/shared.global-service';
import { ServiceSettingsCommonStore } from './service-settings-common.store';
import { ServiceSettingsRiskTypeType } from '@libs/types/src/services/types/service-settings-risk-type.type';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ServiceSettingsUpdateInput } from '@libs/types/src/services/input-type/services/service-settings.update-input';
import { contentGlobalSuccess } from '../../../../content.error';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectServicesState } from '../../../../store/service/service.selectors';
import { map } from 'rxjs/operators';
import { CommonServiceSettingsType } from '@libs/types/src/services/types/common-service-settings.type';
import { TranslatePipe } from '@ngx-translate/core';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { tableImports } from '../../../../../shared/components/kit/table/table.component';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';

@Component({
    selector: 'app-service-settings-common',
    templateUrl: './service-settings-common.component.html',
    providers: [ServiceSettingsCommonStore],
    styles: [`
        .card-header {
            padding: 1.25rem 1.25rem 0 1.25rem;
        }
    `],
    imports: [
        TranslatePipe,
        CheckInputComponent,
        TextInputComponent,
        tableImports,
        ActionButtonsComponent,
    ],
})
export class ServiceSettingsCommonComponent implements OnChanges {
    protected readonly store = inject(ServiceSettingsCommonStore);


    @Input() defaultData: ServiceSettingsCommonObject | undefined = undefined;
    @Input() settingsKey: string = '';

    @Input() hideUseCardInput: boolean = false;
    @Input() hideCustomCardNameInput: boolean = false;
    @Input() hideShareWithOtherServicesInput: boolean = false;

    @Input() title: string = '';

    public additionalContentTemplate = contentChild<TemplateRef<any> | undefined>('additionalContent');
    public footerTemplate = contentChild<TemplateRef<any> | undefined>('footer');

    form = fb.group({
        useCard: fb.control<boolean>(true),
        customCardName: fb.control<string | null>(null),
        shareWithOtherServices: fb.control<number[]>([]),
    });

    commonSettings = toSignal<CommonServiceSettingsType>(globalStore!.select(selectServicesState).pipe(
        map((state) => {
            const serviceSettings = state?.['service']?.serviceSettings;
            const common = serviceSettings
                ?.find((it) => it?.key === this.settingsKey);
            return common?.data ? JSON.parse(common.data) : undefined;
        }),
    ));

    constructor() {
        effect(() => {
            if (this.settingsKey) {
                this.setFormValue();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.defaultData?.currentValue) {
            this.form.patchValue(changes.defaultData.currentValue);
        }
    }

    getFormData(): string {
        const toReturn: ServiceSettingsCommonObject = {
            useCard: !this.hideUseCardInput ? Boolean(this.form.get('useCard')?.value) : true,
            customCardName: !this.hideCustomCardNameInput ? this.form.get('customCardName')?.value || null : null,
            shareWithOtherServices: !this.hideShareWithOtherServicesInput ? this.form.get('shareWithOtherServices')?.value || [] : [],
        };
        return JSON.stringify(toReturn);
    }

    async save() {
        if (isInvalid(this.form) || !this.settingsKey) {
            return;
        }
        const data = this.form.value as ServiceSettingsRiskTypeType;

        const settings: ServiceSettingsUpdateInput = {
            key: this.settingsKey,
            data: JSON.stringify(data),
            serviceId: this.store.serviceId(),
        };
        await this.store.api.updateServiceSettings(settings);
        contentGlobalSuccess.next('common.updated');
    }

    setFormValue() {
        const settings = this.commonSettings();
        if (settings) {
            queueMicrotask(() => {
                this.form.patchValue(settings);
            });
        }
    }
}

interface ServiceSettingsCommonObject {
    useCard?: boolean;
    customCardName?: string | null;
    shareWithOtherServices?: number[];
}
